import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, UsersIcon, BedDoubleIcon, CheckCircle2Icon, HeartPulseIcon, WavesIcon, UtensilsIcon, SparklesIcon, TrendingUpIcon, TargetIcon, PackageIcon, PresentationIcon, RotateCcwIcon, BarChart3Icon, FileTextIcon, PaletteIcon, LayersIcon, ClipboardCheckIcon, GraduationCapIcon, MenuIcon, XIcon, XCircleIcon, QuoteIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
export function ForHotelsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const nav = [{
    label: 'For Hotels',
    href: '#hero'
  }, {
    label: 'How It Works',
    href: '#solution'
  }, {
    label: 'Group Sales',
    href: '#playbook'
  }, {
    label: 'Reports',
    href: '#reporting'
  }, {
    label: 'Pricing',
    href: '#pricing'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-3362">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3363">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-3364">
          <Link to="/" data-id="element-3365">
            <Logo data-id="element-3366" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-3367">
            <ul className="flex items-center gap-8" data-id="element-3368">
              {nav.map(item => <li key={item.label} data-id="element-3369">
                  <a href={item.href} className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-3370">
                    {item.label}
                  </a>
                </li>)}
            </ul>
            <Button variant="primary" data-id="element-3371">Book Hotel Demo</Button>
          </nav>
          <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-id="element-3372">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-3373" /> : <MenuIcon className="w-6 h-6" data-id="element-3374" />}
          </button>
        </div>
        {mobileOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg py-4 px-6 flex flex-col gap-4" data-id="element-3375">
            {nav.map(item => <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-slate-800 py-2 border-b border-slate-50" data-id="element-3376">
                {item.label}
              </a>)}
            <Button variant="primary" className="w-full mt-2" data-id="element-3377">
              Book Hotel Demo
            </Button>
          </div>}
      </header>

      {/* Hero */}
      <section id="hero" className="relative pt-24 pb-16 md:pt-28 md:pb-24 overflow-hidden bg-white" data-id="element-3378">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
        backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
      }} data-id="element-3379" />

        {/* Floating ambient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-3380">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.07] rounded-full blur-3xl" data-id="element-3381" />
          <motion.div animate={{
          x: [0, -25, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-3382" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3383">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3384">
            <div className="lg:col-span-6" data-id="element-3385">
              <FadeIn data-id="element-3386">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm relative overflow-hidden" data-id="element-3387">
                  <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider relative z-10" data-id="element-3388">
                    For Hotels & Resorts
                  </span>
                  <span className="text-sm font-medium text-slate-600 relative z-10" data-id="element-3389">
                    A new layer for group sales
                  </span>
                  <motion.span aria-hidden initial={{
                  x: '-120%'
                }} animate={{
                  x: '320%'
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: 'easeInOut'
                }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent skew-x-12 pointer-events-none" data-id="element-3390" />
                </div>
              </FadeIn>

              <FadeIn delay={0.05} data-id="element-3391">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6 text-brand-navy" data-id="element-3392">
                  Add a{' '}
                  <span className="relative inline-block" data-id="element-3393">
                    <span className="relative z-10" data-id="element-3394">premium experience</span>
                    <motion.span initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: 1
                  }} transition={{
                    duration: 0.9,
                    delay: 0.6,
                    ease: [0.65, 0, 0.35, 1]
                  }} style={{
                    transformOrigin: 'left'
                  }} className="absolute bottom-1 left-0 right-0 h-3 bg-brand-coral/25 -z-0" data-id="element-3395" />
                  </span>{' '}
                  layer to every group booking.
                </h1>
              </FadeIn>

              <FadeIn delay={0.15} data-id="element-3396">
                <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl" data-id="element-3397">
                  Offsite+ helps hotels and resorts turn meetings, retreats, and
                  group stays into structured, engaging, measurable experiences
                  — with AI-designed agendas, attendee intake, wellness
                  preferences, event hubs, and client-ready reports.
                </p>
              </FadeIn>

              <FadeIn delay={0.25} data-id="element-3398">
                <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-3399">
                  <Button variant="primary" size="lg" className="group" data-id="element-3400">
                    Book Hotel Demo
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3401" />
                  </Button>
                  <Button variant="outline" size="lg" href="/designer" data-id="element-3402">
                    Design a Sample Offsite
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.35} data-id="element-3403">
                <div className="border-t border-slate-100 pt-8" data-id="element-3404">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-3405">
                    Designed for hospitality groups
                  </p>
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-slate-400" data-id="element-3406">
                    {['MARRIOTT', 'HILTON', 'IHG', 'ACCOR', 'HYATT'].map(b => <span key={b} className="font-bold text-sm tracking-[0.2em]" data-id="element-3407">
                          {b}
                        </span>)}
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Hotel-specific dashboard */}
            <FadeIn delay={0.4} className="lg:col-span-6 relative lg:h-[640px] flex items-center justify-center" data-id="element-3408">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10 rounded-full blur-3xl" data-id="element-3409" />

              {/* Decorative orbital ring */}
              <motion.div aria-hidden animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="hidden lg:block absolute w-[520px] h-[520px] rounded-full border border-dashed border-slate-200/80" data-id="element-3410">
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_0_4px_rgba(14,165,164,0.15)]" data-id="element-3411" />
                <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-brand-coral" data-id="element-3412" />
              </motion.div>

              {/* Floating accent — top left */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: -10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.1,
              duration: 0.6
            }} className="hidden md:block absolute top-8 -left-2 lg:left-4 z-20" data-id="element-3413">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white rounded-xl shadow-xl shadow-slate-300/40 border border-slate-100 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3414">
                  <div className="bg-emerald-50 p-1.5 rounded-md" data-id="element-3415">
                    <TrendingUpIcon className="w-4 h-4 text-emerald-600" data-id="element-3416" />
                  </div>
                  <div data-id="element-3417">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3418">
                      Rebookings
                    </div>
                    <div className="text-sm font-bold text-brand-navy" data-id="element-3419">
                      +18% YoY
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating accent — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.3,
              duration: 0.6
            }} className="hidden md:block absolute bottom-10 -right-2 lg:right-4 z-20" data-id="element-3420">
                <motion.div animate={{
                y: [0, 8, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-brand-navy text-white rounded-xl shadow-xl shadow-slate-900/20 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3421">
                  <div className="bg-brand-teal/20 p-1.5 rounded-md" data-id="element-3422">
                    <SparklesIcon className="w-4 h-4 text-teal-300" data-id="element-3423" />
                  </div>
                  <div data-id="element-3424">
                    <div className="text-[10px] font-semibold text-teal-300 uppercase tracking-wider" data-id="element-3425">
                      AI Designed
                    </div>
                    <div className="text-sm font-bold" data-id="element-3426">12-min agenda</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              y: 40,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.8
            }} whileHover={{
              y: -6
            }} className="relative w-full max-w-lg mx-auto bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden z-10 transition-shadow hover:shadow-slate-400/40" data-id="element-3427">
                <div className="bg-brand-navy text-white p-5 relative overflow-hidden" data-id="element-3428">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-3429" />
                  <div className="relative z-10" data-id="element-3430">
                    <div className="flex items-center gap-2 mb-1.5" data-id="element-3431">
                      <span className="bg-brand-teal/15 text-teal-300 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-3432">
                        Hotel Group Booking
                      </span>
                    </div>
                    <h3 className="text-xl font-bold" data-id="element-3433">Leadership Retreat</h3>
                    <p className="text-xs text-slate-300 mt-1 flex items-center gap-1" data-id="element-3434">
                      <MapPinIcon className="w-3 h-3" data-id="element-3435" /> Resort Property • Ocean
                      View
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-5 bg-slate-50/50" data-id="element-3436">
                  <div className="grid grid-cols-2 gap-4" data-id="element-3437">
                    <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3" data-id="element-3438">
                      <div className="bg-brand-navy/5 p-2 rounded-lg" data-id="element-3439">
                        <BedDoubleIcon className="w-5 h-5 text-brand-navy" data-id="element-3440" />
                      </div>
                      <div data-id="element-3441">
                        <div className="text-xs text-slate-500 font-medium" data-id="element-3442">
                          Rooms
                        </div>
                        <div className="text-lg font-bold text-brand-navy" data-id="element-3443">
                          48
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3" data-id="element-3444">
                      <div className="bg-brand-coral/10 p-2 rounded-lg" data-id="element-3445">
                        <UsersIcon className="w-5 h-5 text-brand-coral" data-id="element-3446" />
                      </div>
                      <div data-id="element-3447">
                        <div className="text-xs text-slate-500 font-medium" data-id="element-3448">
                          Attendees
                        </div>
                        <div className="text-lg font-bold text-brand-navy" data-id="element-3449">
                          92
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3450">
                    <div className="flex justify-between items-center mb-2" data-id="element-3451">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-3452">
                        Intake Completion
                      </span>
                      <span className="text-sm font-bold text-brand-navy" data-id="element-3453">
                        88%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-3454">
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '88%'
                    }} transition={{
                      duration: 1.2,
                      delay: 0.6
                    }} className="bg-brand-teal h-2 rounded-full" data-id="element-3455" />
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3456">
                    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700" data-id="element-3457">
                      <HeartPulseIcon className="w-4 h-4 text-brand-teal" data-id="element-3458" />
                      Wellness Preferences Captured
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-3459">
                      {[{
                      icon: WavesIcon,
                      label: 'Beach Walk'
                    }, {
                      icon: SparklesIcon,
                      label: 'Spa'
                    }, {
                      icon: UtensilsIcon,
                      label: 'Team Dinner'
                    }].map(a => <span key={a.label} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-md" data-id="element-3460">
                          <a.icon className="w-3 h-3" data-id="element-3461" />
                          {a.label}
                        </span>)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-3462">
                    <div className="flex items-center gap-3" data-id="element-3463">
                      <div className="bg-brand-navy/10 p-2 rounded-md" data-id="element-3464">
                        <BarChart3Icon className="w-4 h-4 text-brand-navy" data-id="element-3465" />
                      </div>
                      <span className="text-sm font-medium text-slate-700" data-id="element-3466">
                        Client Report Preview
                      </span>
                    </div>
                    <span className="flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-3467">
                      <CheckCircle2Icon className="w-3 h-3 mr-1" data-id="element-3468" /> Ready
                    </span>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Property Results Stat Band */}
      <section className="bg-brand-navy py-10 border-y border-brand-navy/80 relative overflow-hidden" data-id="element-3469">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay" data-id="element-3470"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3471">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-white/10" data-id="element-3472">
            {[{
            label: 'Group Rebookings',
            value: '+18%'
          }, {
            label: 'F&B Uplift',
            value: '+22%'
          }, {
            label: 'RevPAR on Groups',
            value: '+14%'
          }, {
            label: 'Net Promoter Score',
            value: '9.2/10'
          }].map((stat, i) => <FadeIn key={stat.label} delay={i * 0.1} className={i !== 0 ? 'pl-8' : ''} data-id="element-3473">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-3474">
                  {stat.value}
                </div>
                <div className="text-xs font-semibold text-brand-teal uppercase tracking-wider" data-id="element-3475">
                  {stat.label}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Problem */}
      <Section className="bg-slate-50" data-id="element-3476">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3477">
          <FadeIn data-id="element-3478">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3479">
              Group clients want more than rooms, meals, and meeting space.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3480">
              Hospitality is competitive. The properties that win bigger,
              repeatable group business sell more than logistics.
            </p>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3481">
          {['Proposals look similar across properties', 'Meeting space is hard to differentiate', 'Clients ask for wellness and engagement', 'Sales teams need stronger premium packages', 'Post-event proof is weak', 'Rebooking signals are missed'].map((p, i) => <FadeIn key={p} delay={i * 0.05} data-id="element-3482">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 h-full flex items-start gap-3" data-id="element-3483">
                <div className="w-2 h-2 rounded-full bg-brand-coral mt-2 shrink-0" data-id="element-3484" />
                <p className="text-slate-700 font-medium" data-id="element-3485">{p}</p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Before / After */}
      <Section className="bg-white" data-id="element-3486">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3487">
          <FadeIn data-id="element-3488">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3489">
              Stop selling square footage. Start selling outcomes.
            </h2>
          </FadeIn>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-3490">
          <FadeIn delay={0.1} data-id="element-3491">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 h-full" data-id="element-3492">
              <div className="flex items-center gap-3 mb-6" data-id="element-3493">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500" data-id="element-3494">
                  <XCircleIcon className="w-5 h-5" data-id="element-3495" />
                </div>
                <h3 className="text-xl font-bold text-slate-700" data-id="element-3496">
                  Standard Group Sales
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-3497">
                {['Generic banquet RFP responses', 'Competing primarily on room rates', 'Standard meeting room setups', 'Manual dietary and activity collection', 'No engagement data to prove ROI'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-600" data-id="element-3498">
                    <XIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-3499" />
                    <span data-id="element-3500">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-3501">
            <div className="bg-brand-navy rounded-2xl p-8 border border-brand-navy shadow-xl h-full relative overflow-hidden" data-id="element-3502">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl" data-id="element-3503" />
              <div className="relative z-10" data-id="element-3504">
                <div className="flex items-center gap-3 mb-6" data-id="element-3505">
                  <div className="w-10 h-10 rounded-full bg-brand-teal/20 flex items-center justify-center text-brand-teal" data-id="element-3506">
                    <CheckCircle2Icon className="w-5 h-5" data-id="element-3507" />
                  </div>
                  <h3 className="text-xl font-bold text-white" data-id="element-3508">
                    With Offsite+
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-3509">
                  {['Branded experience proposals', 'Premium margin add-ons', 'Wellness & networking built-in', 'Automated intake and preferences', 'Client-ready ROI report for rebooking'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-3510">
                      <CheckCircle2Icon className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-3511" />
                      <span data-id="element-3512">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Solution */}
      <Section id="solution" className="bg-slate-50" data-id="element-3513">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3514">
          <FadeIn data-id="element-3515">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3516">
              Offsite+ turns your property into a measurable retreat platform.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-3517">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-200 z-0" data-id="element-3518"></div>
          {[{
          title: 'Before arrival',
          color: 'text-brand-teal',
          bg: 'bg-brand-teal/10',
          items: ['Branded attendee intake', 'Goals and preferences', 'Dietary and wellness notes', 'AI agenda suggestions']
        }, {
          title: 'During stay',
          color: 'text-brand-coral',
          bg: 'bg-brand-coral/10',
          items: ['Digital event hub', 'Agenda and updates', 'Activity signups', 'Wellness options', 'Networking prompts']
        }, {
          title: 'After checkout',
          color: 'text-brand-navy',
          bg: 'bg-brand-navy/10',
          items: ['Client-ready report', 'Satisfaction scores', 'Engagement metrics', 'Rebooking insights']
        }].map((c, i) => <FadeIn key={c.title} delay={i * 0.15} className="relative z-10" data-id="element-3519">
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-slate-100 h-full" data-id="element-3520">
                <div className={`${c.bg} ${c.color} w-12 h-12 rounded-xl mb-5 flex items-center justify-center font-bold`} data-id="element-3521">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-5" data-id="element-3522">
                  {c.title}
                </h3>
                <ul className="space-y-3" data-id="element-3523">
                  {c.items.map(it => <li key={it} className="flex items-center gap-2 text-slate-700 font-medium text-sm" data-id="element-3524">
                      <CheckCircle2Icon className={`w-4 h-4 ${c.color} shrink-0`} data-id="element-3525" />
                      {it}
                    </li>)}
                </ul>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Group Sales Playbook */}
      <Section id="playbook" className="bg-white" data-id="element-3526">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3527">
          <FadeIn data-id="element-3528">
            <div className="inline-flex items-center gap-2 bg-brand-coral/10 text-brand-coral text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-4" data-id="element-3529">
              <LayersIcon className="w-3.5 h-3.5" data-id="element-3530" /> Group Sales Playbook
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3531">
              Ready-to-sell proposal upgrades.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3532">
              Give your sales team pre-packaged experience modules they can drop
              into any RFP response to increase deal size.
            </p>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-3533">
          {[{
          name: 'The Wellness Integration',
          price: '+$1,500',
          desc: 'Capture attendee wellness preferences before arrival and integrate movement, recovery, and healthy F&B into the agenda.',
          icon: HeartPulseIcon
        }, {
          name: 'The Team Connection Package',
          price: '+$2,500',
          desc: 'Add AI group matching, networking prompts, and a digital event hub to keep attendees engaged beyond the meeting room.',
          icon: UsersIcon
        }, {
          name: 'The Executive Retreat Add-On',
          price: '+$3,500',
          desc: 'Full structured experience with learning pathways, facilitator guides, and a comprehensive post-event ROI report.',
          icon: TargetIcon,
          highlight: true
        }].map((play, i) => <FadeIn key={play.name} delay={i * 0.1} data-id="element-3534">
              <div className={`bg-white rounded-2xl p-8 border h-full flex flex-col ${play.highlight ? 'border-brand-coral shadow-lg relative' : 'border-slate-200 shadow-sm'}`} data-id="element-3535">
                {play.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-coral text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-3536">
                    Most Popular
                  </div>}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${play.highlight ? 'bg-brand-coral/10 text-brand-coral' : 'bg-brand-teal/10 text-brand-teal'}`} data-id="element-3537">
                  <play.icon className="w-6 h-6" data-id="element-3538" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-3539">
                  {play.name}
                </h3>
                <div className="text-2xl font-black text-slate-800 mb-4" data-id="element-3540">
                  {play.price}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed flex-1" data-id="element-3541">
                  {play.desc}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* World-Class Content & Frameworks */}
      <Section className="bg-slate-50 border-y border-slate-100" data-id="element-3542">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3543">
          <FadeIn data-id="element-3544">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4" data-id="element-3545">
              Premium content your clients recognize.
            </h2>
            <p className="text-slate-600 mb-10 max-w-2xl mx-auto" data-id="element-3546">
              Every Offsite+ pathway is built from licensed and adapted material
              from top business schools and bestselling authors, elevating your
              property's offering.
            </p>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-3547">
            {['Harvard Business School', 'Wharton Executive Education', 'INSEAD', 'Stanford GSB'].map((src, i) => <FadeIn key={src} delay={i * 0.1} data-id="element-3548">
                <div className="font-serif text-lg md:text-xl text-brand-navy font-medium leading-tight" data-id="element-3549">
                  {src}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Hotel Value */}
      <Section id="value" className="bg-white" data-id="element-3550">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3551">
          <FadeIn data-id="element-3552">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3553">
              Why hotels use Offsite+
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3554">
          {[{
          icon: TrendingUpIcon,
          title: 'Increase group sales value',
          desc: 'Package structured experiences around existing rooms, meeting space, activities, and F&B.'
        }, {
          icon: TargetIcon,
          title: 'Differentiate proposals',
          desc: 'Give sales teams a stronger story than price, location, and amenities.'
        }, {
          icon: HeartPulseIcon,
          title: 'Support wellness-led demand',
          desc: 'Capture attendee preferences for movement, recovery, food, alcohol, and activity style.'
        }, {
          icon: PresentationIcon,
          title: 'Make planners look better',
          desc: 'Give clients cleaner planning, better engagement, and a professional post-event report.'
        }, {
          icon: RotateCcwIcon,
          title: 'Create rebooking conversations',
          desc: 'Use event data, satisfaction, and commitments to open the next booking discussion.'
        }].map((card, i) => <FadeIn key={card.title} delay={i * 0.05} data-id="element-3555">
              <motion.div whileHover={{
            y: -4
          }} className="bg-white p-6 rounded-2xl border border-slate-100 h-full shadow-sm transition-shadow hover:shadow-md" data-id="element-3556">
                <div className="bg-brand-teal/10 text-brand-teal w-12 h-12 rounded-xl flex items-center justify-center mb-5" data-id="element-3557">
                  <card.icon className="w-6 h-6" data-id="element-3558" />
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2" data-id="element-3559">
                  {card.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-3560">
                  {card.desc}
                </p>
              </motion.div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Reporting */}
      <Section id="reporting" className="bg-slate-50" data-id="element-3561">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3562">
          <FadeIn data-id="element-3563">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-3564">
              Give clients a report they can share with leadership.
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-3565">
              Each event ends with a clean, executive-ready report your sales
              team can use to prove ROI and win the next booking.
            </p>
            <Button variant="primary" size="lg" href="/report" data-id="element-3566">
              View Sample Report
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-3567" />
            </Button>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-3568">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-100 p-6 md:p-8" data-id="element-3569">
              <div className="flex items-center justify-between mb-6" data-id="element-3570">
                <h3 className="font-bold text-brand-navy" data-id="element-3571">Sample Report</h3>
                <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-3 py-1 rounded-full" data-id="element-3572">
                  Final
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-3573">
                {[{
                v: '92%',
                l: 'Intake'
              }, {
                v: '84%',
                l: 'Hub Active'
              }, {
                v: '8.7',
                l: 'Satisfaction'
              }].map(m => <div key={m.l} className="bg-slate-50 p-3 rounded-xl text-center" data-id="element-3574">
                    <div className="text-2xl font-black text-brand-navy" data-id="element-3575">
                      {m.v}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1" data-id="element-3576">
                      {m.l}
                    </div>
                  </div>)}
              </div>
              <div className="bg-brand-navy text-white p-4 rounded-xl mb-4 flex items-center justify-between" data-id="element-3577">
                <div className="flex items-center gap-2 text-sm" data-id="element-3578">
                  <TrendingUpIcon className="w-5 h-5 text-brand-teal" data-id="element-3579" />
                  Connection Score
                </div>
                <div className="font-mono" data-id="element-3580">
                  <span className="text-slate-400" data-id="element-3581">6.4</span>
                  <span className="text-brand-teal mx-2" data-id="element-3582">→</span>
                  <span className="font-bold" data-id="element-3583">8.1</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-3584">
                <div className="border border-slate-100 rounded-xl p-3 text-center" data-id="element-3585">
                  <div className="text-xl font-bold text-brand-coral" data-id="element-3586">64%</div>
                  <div className="text-xs text-slate-500 font-medium" data-id="element-3587">
                    Wellness selected
                  </div>
                </div>
                <div className="border border-slate-100 rounded-xl p-3 text-center" data-id="element-3588">
                  <div className="text-xl font-bold text-brand-teal" data-id="element-3589">43</div>
                  <div className="text-xs text-slate-500 font-medium" data-id="element-3590">
                    Commitments
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Partner Model */}
      <Section className="bg-brand-navy text-white" data-id="element-3591">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3592">
          <FadeIn data-id="element-3593">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-3594">
              A white-label experience layer for your group sales team.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3595">
          {[{
          icon: PaletteIcon,
          label: 'Branded hotel/resort event hubs'
        }, {
          icon: LayersIcon,
          label: 'Reusable templates'
        }, {
          icon: FileTextIcon,
          label: 'Proposal support'
        }, {
          icon: TrendingUpIcon,
          label: 'Group sales enablement'
        }, {
          icon: BarChart3Icon,
          label: 'Multi-event dashboard'
        }, {
          icon: HeartPulseIcon,
          label: 'Activity & wellness preference capture'
        }, {
          icon: ClipboardCheckIcon,
          label: 'Reporting templates'
        }, {
          icon: GraduationCapIcon,
          label: 'Team training'
        }].map((f, i) => <FadeIn key={f.label} delay={i * 0.05} data-id="element-3596">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 h-full" data-id="element-3597">
                <div className="bg-brand-teal/20 text-brand-teal w-10 h-10 rounded-lg flex items-center justify-center mb-4" data-id="element-3598">
                  <f.icon className="w-5 h-5" data-id="element-3599" />
                </div>
                <p className="text-sm font-medium text-white" data-id="element-3600">{f.label}</p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="bg-white" data-id="element-3601">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3602">
          <FadeIn data-id="element-3603">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3604">
              Pricing for hotel group business.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-3605">
          {[{
          name: 'Single Event Pilot',
          price: 'From $1,500',
          period: '/ event',
          desc: 'Run one structured event end-to-end.',
          features: ['AI-generated agenda', 'Branded attendee intake', 'Event hub', 'Wellness preferences', 'Post-event report'],
          cta: 'Start a Pilot',
          highlight: false
        }, {
          name: 'Premium Retreat Package',
          price: 'From $3,500',
          period: '/ event',
          desc: 'Best for leadership and destination retreats.',
          features: ['Up to 300 attendees', 'Custom learning modules', 'Facilitator guide', 'Custom wellness track', 'Advanced reporting', '30-day accountability flow', 'White-glove setup'],
          cta: 'Build Premium Retreat',
          highlight: true
        }, {
          name: 'Hotel Partner License',
          price: 'Custom',
          period: 'annual pricing',
          desc: 'For groups running multiple events per year.',
          features: ['White-label branding', 'Multiple events', 'Reusable templates', 'Multi-event dashboard', 'Proposal support', 'Sales team training', 'Priority support'],
          cta: 'Become a Partner',
          highlight: false
        }].map((plan, i) => <FadeIn key={plan.name} delay={i * 0.1} data-id="element-3606">
              <div className={`relative h-full flex flex-col bg-white rounded-2xl p-8 ${plan.highlight ? 'border-2 border-brand-coral shadow-lg scale-105 z-10' : 'border border-slate-200 shadow-sm'}`} data-id="element-3607">
                {plan.highlight && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-brand-coral text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-3608">
                    Recommended
                  </div>}
                <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-3609">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-3" data-id="element-3610">
                  <span className="text-2xl font-black text-slate-800" data-id="element-3611">
                    {plan.price}
                  </span>
                  <span className="text-sm text-slate-500 font-medium" data-id="element-3612">
                    {plan.period}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-6" data-id="element-3613">{plan.desc}</p>
                <ul className="space-y-3 mb-8 flex-1" data-id="element-3614">
                  {plan.features.map(f => <li key={f} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-3615">
                      <CheckCircle2Icon className={`w-4 h-4 shrink-0 mt-0.5 ${plan.highlight ? 'text-brand-coral' : 'text-brand-teal'}`} data-id="element-3616" />
                      <span data-id="element-3617">{f}</span>
                    </li>)}
                </ul>
                <Button variant={plan.highlight ? 'accent' : 'primary'} className="w-full" data-id="element-3618">
                  {plan.cta}
                </Button>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="bg-slate-50" data-id="element-3619">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3620">
          <FadeIn data-id="element-3621">
            <QuoteIcon className="w-12 h-12 text-brand-teal/20 mx-auto mb-6" data-id="element-3622" />
            <h2 className="text-2xl md:text-4xl font-bold text-brand-navy leading-tight mb-8" data-id="element-3623">
              "Offsite+ allowed us to stop selling square footage and start
              selling outcomes. Our group rebooking rate is up 18% because we
              hand the client a report that proves the value of the retreat."
            </h2>
            <div className="flex items-center justify-center gap-4" data-id="element-3624">
              <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden" data-id="element-3625">
                <img src="https://i.pravatar.cc/150?img=47" alt="Avatar" className="w-full h-full object-cover" data-id="element-3626" />
              </div>
              <div className="text-left" data-id="element-3627">
                <div className="font-bold text-brand-navy" data-id="element-3628">Sarah Jenkins</div>
                <div className="text-sm text-slate-500" data-id="element-3629">
                  VP of Group Sales, Luxury Resort Collection
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-brand-navy text-white relative overflow-hidden" data-id="element-3630">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3631">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-brand-teal/20 rounded-full blur-[120px]" data-id="element-3632" />
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-brand-coral/20 rounded-full blur-[120px]" data-id="element-3633" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-3634">
          <FadeIn data-id="element-3635">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance text-white" data-id="element-3636">
              Turn your next group booking into a measurable experience.
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10" data-id="element-3637">
              <Button variant="accent" size="lg" data-id="element-3638">
                Book Hotel Demo
              </Button>
              <Button variant="outline" size="lg" href="/designer" className="border-white/20 text-white hover:bg-white/10 hover:border-white/40" data-id="element-3639">
                Create Sample Offsite
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-10" data-id="element-3640">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-3641">
          <Logo data-id="element-3642" />
          <div className="flex gap-6 text-sm font-medium text-slate-500" data-id="element-3643">
            <Link to="/" className="hover:text-brand-navy transition-colors" data-id="element-3644">
              Home
            </Link>
            <Link to="/designer" className="hover:text-brand-navy transition-colors" data-id="element-3645">
              AI Designer
            </Link>
            <Link to="/report" className="hover:text-brand-navy transition-colors" data-id="element-3646">
              Sample Report
            </Link>
          </div>
          <div className="text-sm text-slate-400" data-id="element-3647">
            © {new Date().getFullYear()} Offsite+. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}